import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import qs from 'qs';
import { Form, FormInstance, TextArea } from '@lightbeamhealth/blueprint-form';
import { produce } from 'immer';
import { reverse } from 'lodash';
import moment, { Moment } from 'moment';
import {
  faCog,
  faCommentsAlt,
  faEllipsisH,
  faExternalLinkAlt,
  faLock,
  faPlus,
  faQuestionCircle,
  faSave,
  faUnlock,
  faExclamationCircle,
  faHistory,
  faEnvelopeOpen,
  faFileAlt,
  faMoon
} from '@fortawesome/pro-solid-svg-icons';
import { Col, Row } from '../../../grid';
import Icon from '../../atoms/icon';
import Button from '../../molecules/button';
import Drawer from '../../molecules/drawer';
import Card, { CardBlock } from '../card';
import BrandSelect from '../brand/select';
import Modal from '../modal';
import Toast from '../toast';
import Tooltip from '../tooltip';
import { Menu, MenuItem } from '../menu';
import { useActiveDialogsUpdater } from '../../../contexts/active-dialogs';
import PrescribeContact from '../../pages/prescribe/contact';
import HeaderBanner from '../../pages/patient/banner';
import ChatHistory from './history';
import { useChats } from '../../../contexts/chats';
import {
  ChatSocket,
  EventNames,
  useChatSocket
} from '../../../contexts/chats/socket';
import {
  IChatThread,
  IChatMessage,
  IChatMessageMedia,
  SystemMessageType,
  EditAssociatedBrand,
  EditThreadContactId,
  MarkThreadUnread,
  MarkThreadUnreadResponse,
  GetThreadResponse,
  EnsureThreadResponse,
  EnsureThread,
  MarkThreadAsReadPatient
} from '../../../libs/chat';
import { Classes } from '@blueprintjs/core';
import { useGetBrands } from '../../../libs/brand';
import { useMetaData } from '../../../contexts/metadata';
import { FormatPhone } from '../../../libs/format';
import { GetFullName, IPatient } from '../../../libs/patient';
import { IContact } from '../../../libs/contact';
import Popover from '../popover';
import { SelectPatientCard } from '../../pages/chat/selectPatient';
import { useTabVisibility } from '../../../libs/hooks/useTabVisibility';

/**
 * Utility functions
 */

const getBackgroundClass = (
  message: IChatMessage,
  isPatientFacing: boolean,
  isSecure: boolean
) => {
  let bgClass = 'bg-white';
  if (
    (isPatientFacing && message.isByPatient) ||
    (!isPatientFacing && !message.isByPatient)
  ) {
    if (isSecure) {
      bgClass = 'bg-light-grey2';
    } else {
      bgClass = 'bg-light-red';
    }
  }
  return bgClass;
};

const getBorderClass = (
  message: IChatMessage,
  isPatientFacing: boolean,
  isSecure: boolean
) => {
  if (
    (!isPatientFacing && message.isByPatient) ||
    (isPatientFacing && !message.isByPatient)
  ) {
    const borderBase = 'border-1';
    let borderColor = 'border-solid border-light-grey';
    if (!isSecure) {
      borderColor = 'border-dotted border-light-red';
    }
    return borderBase + ' ' + borderColor;
  }
  return;
};

const getIcon = (isSecure?: boolean) => {
  let icon = null;
  if (isSecure) {
    icon = getIconMarkup(true);
  } else {
    icon = getIconMarkup(false);
  }
  return icon;
};

const getIconMarkup = (isSecure: boolean) => {
  return isSecure ? (
    <Icon icon={faLock} className="mr-4 text-gray3" />
  ) : (
    <Icon icon={faUnlock} className="mr-4 text-red" />
  );
};

const getSystemText = (
  messageType: IChatMessage['systemMessageType'],
  dateTime: moment.Moment
) => {
  let text = null;
  if (messageType === SystemMessageType.MakeSecure) {
    text = `Secure chat enabled at ${dateTime.format('H:mm A')}.`;
  } else if (messageType === SystemMessageType.MakeInsecure) {
    text = `Direct SMS enabled at ${dateTime.format(
      'H:mm A'
    )}. Avoid sharing PHI over direct SMS.`;
  }
  return text;
};

const getFontClass = (isByPatient: boolean) => {
  let fClass = 'text-sans-md';
  if (!isByPatient) {
    fClass = 'text-sans-sm';
  }
  return fClass;
};

/**
 * Message component
 */

interface MessagePayload {
  data: string;
}

interface SendMessagePayload {
  body: string;
  isSecure: boolean;
}

interface MessageProps {
  message: IChatMessage;
  patient: IChatThread['patient'];
  isPatientFacing: boolean;
  isLastCareManagerMessage?: boolean;
  isInSecureChat: boolean;
  token?: string | null;
}

const getTimeDisplay = (time: Moment): string => {
  const now = moment();
  if (time.isSame(now, 'day')) {
    return time.format('h:mm A');
  }
  return time.format('M/D/YY h:mm A');
};

const formatMessageBody = (body: string): JSX.Element => {
  return (
    <>
      {body.split('\n').map((line, index, array) => (
        <React.Fragment key={index}>
          {line}
          {index < array.length - 1 && <br />}
        </React.Fragment>
      ))}
    </>
  );
};

interface MessageMediaItemProps {
  media: IChatMessageMedia;
  index: number;
  token?: string | null;
}

const MessageMediaItem = ({
  media,
  index,
  token
}: MessageMediaItemProps): JSX.Element => {
  const [hasError, setHasError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const src =
    '/chat/media/?id=' +
    media.id +
    (token ? '&t=' + encodeURIComponent(token) : '');
  const normalizedContentType = (media.contentType || '')
    .split(';')[0]
    .trim()
    .toLowerCase();
  const isImage =
    normalizedContentType.length === 0 ||
    (normalizedContentType.startsWith('image/') &&
      normalizedContentType !== 'image/svg+xml');
  if (!isImage) {
    return (
      <div className="mt-4">
        <a href={src} target="_blank" rel="noopener noreferrer">
          Download attachment
        </a>
      </div>
    );
  }
  if (hasError) {
    // The proxy fetch can fail or time out on a poor connection; show a
    // placeholder so the user knows an image was sent and can try again.
    return (
      <div className="mt-4 pa-4 rounded bg-white text-small">
        <span className="block">
          <Icon icon={faExclamationCircle} className="mr-2" />
          Image couldn&apos;t be loaded.
        </span>
        <div className="mt-2">
          <Button
            size="small"
            onClick={() => {
              setHasError(false);
              setAttempt(attempt + 1);
            }}
          >
            Retry
          </Button>{' '}
          <a href={src} target="_blank" rel="noopener noreferrer">
            Open in new tab
          </a>
        </div>
      </div>
    );
  }
  // Retries get a distinct URL so the browser doesn't reuse the failed
  // response from its in-memory image cache.
  const imgSrc = attempt > 0 ? src + '&r=' + attempt : src;
  return (
    <div className="mt-4">
      <a href={src} target="_blank" rel="noopener noreferrer">
        <img
          key={attempt}
          src={imgSrc}
          alt={`Attached image ${index + 1}`}
          loading="lazy"
          onError={() => setHasError(true)}
          style={{
            maxWidth: '100%',
            maxHeight: 320,
            borderRadius: 4,
            display: 'block'
          }}
        />
      </a>
    </div>
  );
};

// Render inbound MMS media (patient photos). Bytes are served through the
// authenticated /chat/media proxy — the patient-facing view appends its chat
// token; the staff view relies on the session cookie.
const renderMessageMedia = (
  message: IChatMessage,
  token?: string | null
): JSX.Element | null => {
  if (!message.media || message.media.length === 0) {
    return null;
  }
  return (
    <>
      {message.media.map((m, index) => (
        <MessageMediaItem key={m.id} media={m} index={index} token={token} />
      ))}
    </>
  );
};

const Message = ({
  message,
  patient,
  isPatientFacing,
  isLastCareManagerMessage = false,
  isInSecureChat,
  token
}: MessageProps): JSX.Element => {
  let msgWrapper = null;
  const dateTime = moment(message.createdAt);
  const bgClass = getBackgroundClass(
    message,
    isPatientFacing,
    message.isSecure
  );
  const borderClass = getBorderClass(
    message,
    isPatientFacing,
    message.isSecure
  );
  const fClass = getFontClass(message.isByPatient);
  const icon = getIcon(message.isSecure);
  if (message.isSystem) {
    const text = getSystemText(message.systemMessageType, dateTime);
    const msg = (
      <>
        <div
          className={`py-2 px-3 text-center ${bgClass} ${borderClass} ${fClass}`}
        >
          {icon}
          {text}
        </div>
      </>
    );
    msgWrapper = <Col sm={24}>{msg}</Col>;
  } else if (message.isByPatient) {
    const msg = (
      <div className={`pa-4 rounded ${bgClass} ${borderClass}`}>
        <span className="block text-bold text-small">
          {icon} {patient.firstName} {patient.lastName} @{' '}
          {getTimeDisplay(dateTime)}
        </span>
        {message.body ? (
          <p className="mb-0 mt-4 text-bold break-word">
            {formatMessageBody(message.body)}
          </p>
        ) : null}
        {renderMessageMedia(message, token)}
      </div>
    );
    if (isPatientFacing) {
      msgWrapper = (
        <Col sm={16} offset={{ sm: 8 }}>
          {msg}
        </Col>
      );
    } else {
      msgWrapper = <Col sm={16}>{msg}</Col>;
    }
  } else {
    // Show read receipt only for the last care manager message
    const readReceiptElement = !isPatientFacing &&
      isLastCareManagerMessage &&
      isInSecureChat &&
      message.isSecure && (
        <div className="mt-2 text-small text-muted">
          {message.isRead ? (
            <span>Read • {moment(message.readAt).format('h:mm A')}</span>
          ) : (
            <span>Unread</span>
          )}
        </div>
      );
    const msg = (
      <div className={`pa-4 rounded ${bgClass} ${borderClass}`}>
        <span className="block text-bold text-small">
          {icon}{' '}
          {isPatientFacing ? 'Your Care Team' : message.user?.email || ''} @{' '}
          {getTimeDisplay(dateTime)}
        </span>
        {message.body ? (
          <p className="mb-0 mt-4 text-bold break-word">
            {formatMessageBody(message.body)}
          </p>
        ) : null}
        {renderMessageMedia(message, token)}
        {readReceiptElement}
      </div>
    );
    if (isPatientFacing) {
      msgWrapper = <Col sm={16}>{msg}</Col>;
    } else {
      msgWrapper = (
        <Col sm={16} offset={{ sm: 8 }}>
          {msg}
        </Col>
      );
    }
  }
  return <Row className="mb-4">{msgWrapper}</Row>;
};

/**
 * Messages Container
 */

interface MessagesContainerProps {
  isPatientFacing: boolean;
}

export const MessagesContainer: React.FC<MessagesContainerProps> = ({
  isPatientFacing,
  children
}): JSX.Element => {
  // Styles
  const patientFacingStyles: React.CSSProperties = {
    height: 'calc(100vh - 102px)',
    paddingBottom: '80px',
    paddingTop: '77px' // height of the header
  };
  const userFacingStyles: React.CSSProperties = {
    height: 'calc(100% - 145px)',
    paddingBottom: '140px'
  };
  const style = isPatientFacing ? patientFacingStyles : userFacingStyles;
  // CSS
  const commonCSS = 'bg-white overflow-y-auto overflow-x-hidden';
  const patientFacingCSS = `${commonCSS} pl-4 pr-4 pb-4`;
  const userFacingCSS = `${commonCSS} pa-4`;
  const css = isPatientFacing ? patientFacingCSS : userFacingCSS;
  return (
    <div className={css} style={style}>
      {children}
    </div>
  );
};

const MessageScrollBottom = () => {
  const elementRef = useRef<HTMLDivElement>();
  useEffect(() => {
    if (elementRef.current) {
      elementRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  });
  return <div ref={elementRef} />;
};

/**
 * Messages component
 */

interface MessagesProps {
  messages: IChatMessage[];
  cutoff: string;
  patient: IChatThread['patient'];
  isPatientFacing: boolean;
  loadMoreMessages: () => void;
  isLoadingMore: boolean;
  isInSecureChat: boolean;
  token?: string | null;
}

const Messages = ({
  messages,
  cutoff,
  patient,
  isPatientFacing,
  loadMoreMessages,
  isLoadingMore,
  isInSecureChat,
  token
}: MessagesProps): JSX.Element => {
  return (
    <React.Fragment>
      {cutoff && cutoff !== NO_MORE_MESSAGES ? (
        <Row>
          <Col xs={24} className="text-center pt-4 pb-6">
            <Button
              intent="primary"
              onClick={loadMoreMessages}
              disabled={isLoadingMore}
              size="small"
            >
              <Icon icon={faPlus} /> Load More Messages
            </Button>
          </Col>
        </Row>
      ) : null}
      {(() => {
        // Find the last care manager message index
        const lastCareManagerMessageIndex = messages.findLastIndex(
          (msg) => !msg.isByPatient && !msg.isSystem
        );
        return messages.map((message, index) => {
          const isLastCareManagerMessage =
            !message.isByPatient && index === lastCareManagerMessageIndex;

          return (
            <Message
              key={message.id}
              message={message}
              patient={patient}
              isPatientFacing={isPatientFacing}
              isLastCareManagerMessage={isLastCareManagerMessage}
              isInSecureChat={isInSecureChat}
              token={token}
            />
          );
        });
      })()}
      {!cutoff || !cutoff.length ? <MessageScrollBottom /> : null}
    </React.Fragment>
  );
};

interface ChatButtonProps {
  onlyShowUnreadIndicator?: boolean;
  numUnread: IChatThread['numUnread'];
  setIsChatOpen: (isChatOpen: boolean) => void;
}

const ChatButton = ({
  onlyShowUnreadIndicator = false,
  numUnread,
  setIsChatOpen
}: ChatButtonProps): JSX.Element => {
  const unreadComponent = (
    <div className="bg-intent-primary text-white rounded inline-block text-center py-1 px-3">
      {numUnread}
    </div>
  );
  if (onlyShowUnreadIndicator) {
    return (
      <Button
        className="pt-3 text-small"
        variant="minimal"
        onClick={() => setIsChatOpen(true)}
      >
        <Icon
          icon={faCommentsAlt}
          className={`text-small ${numUnread > 0 ? 'mr-3' : ''}`}
        />
        {numUnread > 0 ? unreadComponent : null}
      </Button>
    );
  }
  return (
    <Button
      className="pt-3 text-small"
      variant="minimal"
      onClick={() => setIsChatOpen(true)}
    >
      <Icon icon={faCommentsAlt} className="text-small mr-4" />
      <div className={`dotted inline-block ${numUnread > 0 ? 'mr-3' : ''}`}>
        Message
      </div>
      {numUnread > 0 ? unreadComponent : null}
    </Button>
  );
};

/**
 * Chat UI
 */

/**
 * Chat UI Helper Components
 */

interface ChatSettingsProps {
  thread: IChatThread;
  isOpen: boolean;
  onClose: () => void;
}

const ChatSettings = ({
  thread,
  isOpen,
  onClose
}: ChatSettingsProps): JSX.Element => {
  const { isLoading, brands } = useGetBrands({
    page: 0,
    limit: 20
  });
  const initialBrand = thread && thread.brand ? thread.brand : null;
  const { updateThread } = useChats();
  const [contact, setContact] = useState<IContact>(null);
  const [brand, setBrand] = useState(initialBrand);
  const brandId = brand ? brand.id.toString() : '';

  const handleSubmit = () => {
    /**
     * Update all chat settings. We nest
     * the updates so that we only have
     * to output one success message.
     *
     * Opportunity in the future to refactor
     * so that we don't have to make two
     * API calls.
     */
    EditAssociatedBrand({
      patientId: thread.patientId,
      brandId: brand.id
    }).then((response) => {
      if (response.status !== 'OK') {
        Modal.error({
          title: 'Error Updating Brand',
          content: response.message
        });
        return;
      }
      EditThreadContactId({
        patientId: thread.patientId,
        contactId: contact?.id
      }).then((response) => {
        if (response.status !== 'OK') {
          Modal.error({
            title: 'Error Updating Chat Provider Contact',
            content: response.message
          });
          return;
        }
        Toast({
          intent: 'success',
          message: 'Chat settings successfully updated!'
        });
        const updatedThread = (response as GetThreadResponse)?.thread;
        updateThread(updatedThread);
        onClose();
      });
    });
  };

  return (
    <Drawer
      title={
        <span>
          <Icon className="mr-4" icon={faCog} />
          Chat Settings
        </span>
      }
      isOpen={isOpen}
      onClose={onClose}
      portalClassName="z-201"
    >
      <div className={Classes.DRAWER_BODY}>
        <CardBlock>
          <Row>
            <Col xs={24}>
              <span className="block text-sans-md font-bold mb-6">
                Change Chat Brand
              </span>
              <div className="mb-6">
                <BrandSelect
                  onChange={(brand) => setBrand(brand)}
                  brands={brands}
                  isLoading={isLoading}
                  value={brandId}
                  fill
                />
              </div>
              {brand ? (
                <Card className="pa-4 text-center">
                  {brand.logo && brand.logo.length ? (
                    <img
                      src={brand.logo}
                      className="sharp-image mb-4"
                      style={{
                        maxHeight: '44px'
                      }}
                      alt=""
                    />
                  ) : null}
                  <span className="block">{brand.name}</span>
                  <span className="block">{FormatPhone(brand.en)}</span>
                </Card>
              ) : (
                <p>
                  There is no brand associated with this chat at this time. If
                  you want to add a brand, you can configure it in your account
                  settings.
                </p>
              )}
            </Col>
          </Row>
        </CardBlock>
        <CardBlock>
          <Row>
            <Col xs={24}>
              <span className="block text-sans-md font-bold mb-6">
                Assign Provider Contact to Chat
              </span>
              <div className="mb-6">
                <PrescribeContact
                  isActive={true}
                  className={'mb-8'}
                  contact={contact}
                  setContact={setContact}
                  chatThread={thread}
                  interactive={false}
                  hideHeader
                />
              </div>
            </Col>
          </Row>
        </CardBlock>
      </div>
      <div className={Classes.DRAWER_FOOTER}>
        <Row>
          <Col xs={24}>
            <Button
              intent="success"
              disabled={!brand}
              fill
              onClick={handleSubmit}
            >
              <Icon icon={faSave} className="mr-2" /> Save
            </Button>
          </Col>
        </Row>
      </div>
    </Drawer>
  );
};

interface MarkAsUnreadProps {
  thread: IChatThread;
  isEnabled: boolean;
  setIsEnabled: (isEnabled: boolean) => void;
  setWasManuallyUnread: (val: boolean) => void;
}

const MarkAsUnread = ({
  thread,
  isEnabled,
  setIsEnabled,
  setWasManuallyUnread
}: MarkAsUnreadProps): JSX.Element => {
  const { updateThread } = useChats();
  const threadRef = useRef(thread);
  useEffect(() => {
    if (!isEnabled) {
      threadRef.current = thread;
    }
  }, [thread, isEnabled]);

  const unread = () => {
    const lastReadByUserAt = threadRef.current?.lastReadByUserAt;
    const numUnread = threadRef.current?.numUnread;
    MarkThreadUnread({
      patientId: thread.patientId,
      previousLastReadAt: lastReadByUserAt,
      previousNumUnread: numUnread
    }).then((response) => {
      if (response.status !== 'OK') {
        Modal.error({
          title: 'Error Marking Thread As Unread',
          content: response.message
        });
        return;
      }
      updateThread((response as MarkThreadUnreadResponse).thread);
      setIsEnabled(false);
      setWasManuallyUnread(true);
      Toast({
        intent: 'success',
        message: 'The chat has been marked unread!'
      });
    });
  };

  return (
    <Button
      className="mb-4 ml-3"
      variant="minimal"
      bare
      onClick={unread}
      disabled={isEnabled === false}
    >
      <span className="dotted">Mark Unread</span>
      <Tooltip content="A chat is marked as read after 10 seconds or if the message box below is clicked. Once read, this button will be enabled.">
        <Icon icon={faQuestionCircle} color="text-gray3" className="ml-2" />
      </Tooltip>
    </Button>
  );
};

const getSendButtonText = (isPatientFacing: boolean, isSecure: boolean) => {
  if (isPatientFacing) {
    return 'Send';
  }
  return isSecure ? 'Send Secure Message' : 'Send Text Message';
};

const getInputPlaceholder = (isPatientFacing: boolean, isSecure: boolean) => {
  if (isPatientFacing) {
    return 'Enter Your Message';
  }
  return isSecure ? 'Enter Secure Message' : 'Enter Unsecure Message';
};

interface ChatInfoProps {
  isSecure: boolean;
}

const ChatInfo = ({ isSecure }: ChatInfoProps) => {
  const { terms } = useMetaData();

  const inner = (
    <React.Fragment>
      <Icon
        icon={isSecure ? faLock : faUnlock}
        color={isSecure ? 'gray3' : 'red'}
        className="text-small mr-4 ml-4"
      />
      {isSecure
        ? 'Chat is secure. You may share PHI.'
        : 'Chat is unsecure. Do not share PHI.'}
    </React.Fragment>
  );

  if (isSecure) {
    return (
      <Tooltip
        content={`Secure chat links expire every 4 hours. To access the secure chat again, ${terms.patient.plural} can text the word 'secure' to have a new link sent to them.`}
      >
        {inner}
        <Icon icon={faQuestionCircle} className="ml-2" />
      </Tooltip>
    );
  }

  return inner;
};

interface ChatHistoryDrawerProps {
  patientId: number;
  isOpen: boolean;
  onClose: () => void;
}

const ChatHistoryDrawer = ({
  patientId,
  isOpen,
  onClose
}: ChatHistoryDrawerProps): JSX.Element => {
  return (
    <Drawer
      title={
        <span>
          <Icon className="mr-4" icon={faHistory} />
          Chat History
        </span>
      }
      isOpen={isOpen}
      onClose={onClose}
      portalClassName="z-201"
    >
      <div className={Classes.DRAWER_BODY}>
        <ChatHistory patientId={patientId} />
      </div>
    </Drawer>
  );
};

interface ChatActionsMenuProps {
  thread: IChatThread;
  patient: IPatient;
  useTemplate: (data: string) => void;
}

const ChatActionsMenu = ({
  thread,
  patient,
  useTemplate
}: ChatActionsMenuProps): JSX.Element => {
  const { terms } = useMetaData();
  const addDialog = useActiveDialogsUpdater();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const handleUseTemplate = () => {
    addDialog({
      id: `pushMessage${patient.id}`,
      name: 'PushMessage',
      payload: {
        patient,
        templateType: 'chat',
        useTemplate
      }
    });
  };

  return (
    <>
      <Popover
        position="bottom-left"
        usePortal={false}
        content={
          <Menu>
            <MenuItem
              text="Settings"
              icon={<Icon icon={faCog} />}
              onClick={() => setSettingsOpen(true)}
            />
            <MenuItem
              text="History"
              icon={<Icon icon={faHistory} />}
              onClick={() => setHistoryOpen(true)}
            />
            <MenuItem
              text="Use Chat Template"
              icon={<Icon icon={faFileAlt} />}
              onClick={handleUseTemplate}
            />
            {thread.isInHours === false ? (
              <>
                <MenuItem
                  text="After Hours Message"
                  icon={<Icon icon={faMoon} />}
                  disabled
                  labelElement={
                    <Tooltip
                      content={`It is after business hours. After hours messaging sent in response to a ${terms.patient.default} texting in is: ${thread?.contact?.afterHoursMessages?.chat}`}
                    >
                      <Icon icon={faQuestionCircle} color="text-gray3" />
                    </Tooltip>
                  }
                />
              </>
            ) : null}
          </Menu>
        }
      >
        <Button
          className="mb-4 ml-3"
          variant="minimal"
          bare
          aria-label="Chat Actions"
          style={{ outline: 'none', boxShadow: 'none' }}
        >
          <Icon icon={faEllipsisH} />
        </Button>
      </Popover>
      <ChatSettings
        thread={thread}
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
      <ChatHistoryDrawer
        patientId={thread.patientId}
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
    </>
  );
};

interface CraftMessageProps {
  thread: IChatThread;
  isPatientFacing: boolean;
  isInDrawer: boolean;
  isSecure: boolean;
  toggleIsSecure: () => void;
  isMarkUnreadEnabled: boolean;
  setIsMarkUnreadEnabled: (isEnabled: boolean) => void;
  sendMessage: (payload: SendMessagePayload) => void;
  optedOut: boolean;
}

const CraftMessage = ({
  thread,
  isPatientFacing,
  isInDrawer,
  isSecure,
  toggleIsSecure,
  isMarkUnreadEnabled,
  setIsMarkUnreadEnabled,
  sendMessage,
  optedOut
}: CraftMessageProps): JSX.Element => {
  const { updateThreadAsRead } = useChats();
  const [wasManuallyUnread, setWasManuallyUnread] = useState(false);
  useEffect(() => {
    setWasManuallyUnread(false);
  }, [thread.id]);
  const patient = thread?.patient || null;
  const form = new FormInstance<MessagePayload>({
    data: ''
  });
  const leftColSpan = isPatientFacing ? 24 : 24;
  const backgroundCSS = isSecure ? 'bg-light-grey2' : 'bg-light-red';
  let positionCSS = 'absolute';
  if (isPatientFacing) {
    positionCSS = 'fixed w-full';
  } else {
    if (isInDrawer) {
      positionCSS = positionCSS.concat(' w-full');
    } else {
      positionCSS = positionCSS.concat(' inset-col-pad');
    }
  }

  const useTemplate = (data: string) => {
    form.setData({ data });
  };

  const onSubmit = (message: MessagePayload) => {
    if (!message.data) {
      return;
    }
    sendMessage({
      body: message.data.trim(),
      isSecure
    });
    form.getForm().resetForm();
  };

  const onChatFocus = () => {
    if (thread.numUnread > 0 && !wasManuallyUnread) {
      updateThreadAsRead(thread.id);
      setIsMarkUnreadEnabled(true);
    }
  };

  return (
    <div
      className={`pa-3 ${positionCSS} bottom-0 ${backgroundCSS}`}
      onFocus={onChatFocus}
    >
      {isPatientFacing ? null : (
        <>
          <ChatInfo isSecure={isSecure} />
          <Button
            className="mb-4 ml-3"
            variant="minimal"
            bare
            onClick={toggleIsSecure}
          >
            <span className="dotted">
              {isSecure
                ? 'Switch to Text Message (Unsecure)'
                : 'Switch to Secure Messaging'}
            </span>
          </Button>
          <MarkAsUnread
            thread={thread}
            isEnabled={isMarkUnreadEnabled}
            setIsEnabled={setIsMarkUnreadEnabled}
            setWasManuallyUnread={setWasManuallyUnread}
          />
          <ChatActionsMenu
            thread={thread}
            patient={patient}
            useTemplate={useTemplate}
          />
        </>
      )}
      <Row nogutter>
        <Col sm={leftColSpan}>
          <Form form={form} onSubmit={onSubmit}>
            <Row nogutter>
              <Col xs={18}>
                {isPatientFacing && thread?.isInHours === false ? (
                  <HeaderBanner
                    icon={faExclamationCircle}
                    title={'Notice:'}
                    className="bg-light-orange"
                  >
                    <span>
                      It is after business hours. Responses may be delayed.
                    </span>
                  </HeaderBanner>
                ) : null}
                <TextArea
                  bare
                  fill
                  name="data"
                  placeholder={getInputPlaceholder(isPatientFacing, isSecure)}
                  label="Message"
                  autoComplete="off"
                />
              </Col>
              <Col
                xs={6}
                style={{
                  paddingLeft: '10px'
                }}
              >
                <Tooltip
                  disabled={!(optedOut && !isPatientFacing)}
                  content="Has opted out of the chat"
                  usePortal={false}
                >
                  <Button
                    intent="primary"
                    type="submit"
                    fill
                    disabled={optedOut && !isPatientFacing}
                    style={{
                      marginTop: '9px',
                      marginRight: '9px'
                    }}
                  >
                    {getSendButtonText(isPatientFacing, isSecure)}
                  </Button>
                </Tooltip>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

/**
 * Main Chat UI Component
 */

interface Listener {
  event: any;
  handler: (...args: any) => any;
}

interface ChatUIProps {
  thread: IChatThread;
  isPatientFacing?: boolean;
  isInDrawer?: boolean;
}

const NO_MORE_MESSAGES = 'no_more';

export const ChatUI = ({
  thread,
  isPatientFacing = false,
  isInDrawer = false
}: ChatUIProps): JSX.Element => {
  const { updateThreadAsRead, isSecure: isDefaultSecure } = useChats();
  const [isSecure, setIsSecure] = useState(isDefaultSecure || false);
  const patient = thread?.patient || null;
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [cutoff, setCutoff] = useState<string>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isMarkUnreadEnabled, setIsMarkUnreadEnabled] = useState(false);
  const lastSoundPlayed = useRef(null);
  const [optedOut, setOptedOut] = useState<boolean>(
    thread?.isOptedOut || false
  );

  const threadRef = useRef(thread);
  useEffect(() => {
    threadRef.current = thread;
  }, [thread?.id]);

  useEffect(() => {
    setOptedOut(thread?.isOptedOut || false);
  }, [thread?.isOptedOut]);

  const toggleIsSecure = () => {
    setIsSecure((prevIsSecure) => !prevIsSecure);
  };

  // Socket event handlers
  const socket = useChatSocket();

  // Read receipt functionality
  const { search } = useLocation();
  const query = qs.parse(search, { ignoreQueryPrefix: true });
  const token = query && query.t ? (query.t as string) : null;
  const [_, setLastReadThreadId] = useState<number | null>(null);
  const markReadDebounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  // Check if thread has unread messages from care managers
  const hasUnreadCMMessages = messages.some(
    (msg) =>
      !msg.isByPatient &&
      (!thread.lastReadByPatientAt ||
        msg.createdAt > thread.lastReadByPatientAt)
  );
  const markThreadAsRead = useCallback(() => {
    if (markReadDebounceRef.current) {
      clearTimeout(markReadDebounceRef.current);
    }
    markReadDebounceRef.current = setTimeout(() => {
      if (!isPatientFacing || !threadRef.current) {
        return;
      }
      const currentThreadId = threadRef.current.id;
      // Emit over socket if available
      if (socket && socket.connected) {
        try {
          socket.emit(EventNames.MARK_THREAD_READ, currentThreadId);
        } catch (e) {
          console.warn('socket emit failed for MARK_THREAD_READ', e);
        }
      }
      // Always backstop with HTTP when token present (ensures server updates/broadcasts)
      if (token) {
        MarkThreadAsReadPatient({
          token,
          threadId: currentThreadId
        })
          .then(() => {
            setLastReadThreadId(currentThreadId);
          })
          .catch((error) => {
            console.warn('Failed to mark thread as read (HTTP):', error);
          });
      } else if (socket && socket.connected) {
        setLastReadThreadId(currentThreadId);
      }
    }, 250);
  }, [isPatientFacing, socket, token]);

  // Use tab visibility hook for patient-facing mode
  // triggerOnMount: true means it will also fire when the page loads if tab is visible
  useTabVisibility({
    onBecomeVisible: isPatientFacing ? markThreadAsRead : undefined,
    debounceMs: 300,
    triggerOnMount: true
  });

  const prevUnreadRef = useRef<boolean>(hasUnreadCMMessages);
  useEffect(() => {
    if (isPatientFacing && hasUnreadCMMessages && !prevUnreadRef.current) {
      setLastReadThreadId(null);
    }
    prevUnreadRef.current = hasUnreadCMMessages;
  }, [hasUnreadCMMessages, isPatientFacing]);

  const processListeners = (action: 'on' | 'off', socket: ChatSocket) => {
    const listeners: Listener[] = [
      {
        event: EventNames.ENTER_PATIENT,
        handler: onPatientEnter
      },
      {
        event: EventNames.LIST_MESSAGES_SUCCESS,
        handler: onListMessagesSuccess
      },
      {
        event: EventNames.NEW_MESSAGE,
        handler: onNewMessage
      },
      {
        event: EventNames.ENTER_FAIL,
        handler: onEnterFail
      },
      {
        event: EventNames.NEW_MESSAGE_FAIL,
        handler: onNewMessageFail
      },
      {
        event: EventNames.LIST_MESSAGES_FAIL,
        handler: onListMessagesFail
      }
    ];
    if (!isPatientFacing) {
      listeners.push({
        event: EventNames.THREAD_READ,
        handler: onThreadRead
      });
    }
    for (const listener of listeners) {
      socket[action](listener.event, listener.handler);
    }
  };

  const onPatientEnter = () => {
    return;
  };

  const onListMessagesSuccess = (payload) => {
    const { patientId, messages: newMessages, cutoff } = payload;
    if (patientId !== threadRef.current?.patientId) {
      return;
    }
    setIsLoadingMore(false);
    setMessages((messages) => {
      const normalized = reverse(newMessages).map((m) => ({
        ...m,
        createdAt: new Date(m.createdAt),
        readAt: m.readAt ? new Date(m.readAt) : null,
        isRead: m.isRead
      }));
      const combined = messages.length
        ? [...normalized, ...messages]
        : normalized;
      // On user view, set the last CM message's isRead based on thread.lastReadByPatientAt
      if (!isPatientFacing && combined.length > 0) {
        const lastIdx = [...combined]
          .map((mm) => mm.isByPatient)
          .lastIndexOf(false);
        if (lastIdx !== -1) {
          const lastReadTs = threadRef.current?.lastReadByPatientAt
            ? new Date(threadRef.current.lastReadByPatientAt)
            : null;
          if (lastReadTs) {
            const msg = combined[lastIdx];
            const shouldBeRead = msg.createdAt <= lastReadTs;
            combined[lastIdx] = {
              ...msg,
              isRead: shouldBeRead,
              readAt: shouldBeRead ? msg.readAt || lastReadTs : msg.readAt
            };
          }
        }
      }
      return combined;
    });
    setCutoff(cutoff ? cutoff : NO_MORE_MESSAGES);
    // After initial load in patient view, if there are unread care-manager messages and we're visible, mark as read
    if (isPatientFacing) {
      const lastReadTs = thread?.lastReadByPatientAt
        ? new Date(thread.lastReadByPatientAt)
        : null;
      const anyUnreadFromCM = (newMessages || []).some((msg) => {
        if (msg.isByPatient) return false;
        if (!lastReadTs) return true;
        return new Date(msg.createdAt) > lastReadTs;
      });
      if (anyUnreadFromCM && document.visibilityState === 'visible') {
        setTimeout(() => {
          markThreadAsRead();
        }, 0);
      }
    }
  };

  const onNewMessage = (message: IChatMessage) => {
    if (isPatientFacing && !message.isByPatient) {
      setLastReadThreadId(null);
      // If the patient chat is currently visible, immediately mark the thread as read after the new message
      // is added to state.
      if (document.visibilityState === 'visible') {
        setTimeout(() => {
          markThreadAsRead();
        }, 0);
      }
    }
    setMessages((messages) => {
      if (message.patientId !== threadRef.current?.patientId) {
        return messages;
      }
      const newMessages = produce(messages, (draft) => {
        const shouldShowAsUnread = !message.isByPatient && !isPatientFacing;
        const newMessage = {
          ...message,
          createdAt: new Date(message.createdAt),
          readAt: message.readAt ? new Date(message.readAt) : null,
          isRead: shouldShowAsUnread ? false : message.isRead
        };
        draft.push(newMessage);
        draft.sort((a, b) => {
          return a.createdAt.valueOf() - b.createdAt.valueOf();
        });
      });
      return newMessages;
    });
    setCutoff((oldCutoff) => {
      if (oldCutoff === NO_MORE_MESSAGES) {
        return null;
      }
      return oldCutoff;
    });
    let isAfterCutoff = true;
    /**
     * Play a sound to indicate that
     * there's a new message from the patient
     */
    const now = moment();
    if (lastSoundPlayed.current !== null) {
      const dateTimeCutoff = moment(lastSoundPlayed.current).add(1, 'minute');
      isAfterCutoff = now.isAfter(dateTimeCutoff);
    }
    if (!isAfterCutoff) {
      return;
    }
    /**
     * Play a sound notifying of the new message. For the CS user,
     * the sound will play when the patient sends in a new message.
     * For the Patient, the sound will play when the CS user
     * sends in a new message.
     */
    if (
      (message.isByPatient && !isPatientFacing) ||
      (!message.isByPatient && isPatientFacing)
    ) {
      const audio = new Audio('/static/audio/clearly.mp3');
      /**
       * play() returns a promise and can fail if the user
       * hasn't interacted with the page in some way. By
       * not handling this scenario, we're allowing this
       * feature to fail silently at this time.
       */
      audio.play();
      lastSoundPlayed.current = now.format();
    }
  };

  const onEnterFail = (error: string) => {
    Modal.error({
      title: 'Failed to Enter Chat',
      content: error
    });
  };

  const onNewMessageFail = (error: string) => {
    Modal.error({
      title: 'Failed to Send Chat Message',
      content: error
    });
  };

  const onListMessagesFail = (error: string) => {
    // Stop loading since we failed to list messages
    setIsLoadingMore(false);
    Modal.error({
      title: 'Failed to Load Chat Messages',
      content: error
    });
  };

  const onThreadRead = (threadId: number, readAt: Date) => {
    setMessages((messages) => {
      return messages.map((msg) => {
        if (msg.threadId === threadId && !msg.isByPatient) {
          return {
            ...msg,
            isRead: true,
            readAt
          };
        }
        return msg;
      });
    });
  };

  // Sync the last CM message's read status from thread context updates (backup if event missed)
  useEffect(() => {
    if (isPatientFacing || !thread?.lastCMMessage) {
      return;
    }
    setMessages((msgs) => {
      if (!msgs || msgs.length === 0) {
        return msgs;
      }
      const lastIdx = [...msgs].map((m) => m.isByPatient).lastIndexOf(false);
      if (lastIdx === -1) {
        return msgs;
      }
      const last = msgs[lastIdx];
      const shouldBeRead = thread.lastCMMessage.isRead === true;
      const readAtFromThread = thread.lastCMMessage.readAt
        ? new Date(thread.lastCMMessage.readAt)
        : last.readAt;
      if (last.isRead === shouldBeRead && (!shouldBeRead || last.readAt)) {
        return msgs;
      }
      const next = [...msgs];
      next[lastIdx] = {
        ...last,
        isRead: shouldBeRead,
        readAt: shouldBeRead ? last.readAt || readAtFromThread : last.readAt
      };
      return next;
    });
  }, [thread?.lastCMMessage?.isRead, thread?.lastCMMessage?.readAt]);

  const sendMessage = (payload: SendMessagePayload) => {
    if (isPatientFacing) {
      socket.emit(EventNames.NEW_MESSAGE_TO_USER, payload);
      // When the patient sends a message, consider the thread "viewed" and
      // attempt to mark as read (debounced) if there are unread CM messages.
      // Reset the last-read guard for subsequent reads
      setLastReadThreadId(null);
      markThreadAsRead();
    } else {
      socket.emit(EventNames.NEW_MESSAGE_TO_PATIENT, {
        ...payload,
        patientId: thread.patientId
      });
    }
  };

  const loadMoreMessages = () => {
    if (socket) {
      socket.emit(EventNames.LIST_MESSAGES, thread.patientId, 10, cutoff);
    }
    setIsLoadingMore(socket ? true : false);
  };

  // Reload the messages & reset the secure setting when thread changes
  useEffect(() => {
    setIsSecure(isDefaultSecure || false);
    if (socket) {
      if (isPatientFacing) {
        socket.emit(EventNames.ENTER_PATIENT);
      } else if (thread?.patientId) {
        socket.emit(EventNames.ENTER_USER, [thread.patientId]);
      }
      setMessages([]);
      setCutoff(null);
      socket.emit(EventNames.LIST_MESSAGES, thread.patientId, 10);
    }
    /**
     * Wait 10 seconds before marking a
     * text thread as read to avoid
     * acting prematurely (for example if
     * the user is just cycling through text threads
     * but not actually reading them).
     */
    let timer = null;
    if (thread && !isPatientFacing) {
      /**
       * This call resets the mark unread
       * button as as a user goes from chat
       * to chat
       */
      setIsMarkUnreadEnabled(false);
      timer = setTimeout(() => {
        if (thread.numUnread && thread.numUnread > 0) {
          updateThreadAsRead(thread.id);
        }
        setIsMarkUnreadEnabled(true);
      }, 10000);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [thread?.id]);

  // Setup the socket listeners
  useEffect(() => {
    if (socket) {
      processListeners('on', socket);
    }
    return () => {
      if (socket) {
        if (isPatientFacing) {
          socket.emit(EventNames.LEAVE_PATIENT);
        }
        processListeners('off', socket);
      }
      if (markReadDebounceRef.current) {
        clearTimeout(markReadDebounceRef.current);
      }
    };
  }, [socket]);
  useEffect(() => {
    if (socket && isPatientFacing) {
      socket.emit(EventNames.ENTER_PATIENT);
    }
  }, [socket?.id, isPatientFacing]);
  return (
    <>
      <MessagesContainer isPatientFacing={isPatientFacing}>
        <Messages
          messages={messages}
          cutoff={cutoff}
          patient={patient}
          isPatientFacing={isPatientFacing}
          loadMoreMessages={loadMoreMessages}
          isLoadingMore={isLoadingMore}
          isInSecureChat={isSecure}
          token={token}
        />
      </MessagesContainer>
      <CraftMessage
        thread={thread}
        isPatientFacing={isPatientFacing}
        isInDrawer={isInDrawer}
        sendMessage={sendMessage}
        isSecure={isSecure}
        toggleIsSecure={toggleIsSecure}
        isMarkUnreadEnabled={isMarkUnreadEnabled}
        setIsMarkUnreadEnabled={setIsMarkUnreadEnabled}
        optedOut={optedOut}
      />
    </>
  );
};

/**
 * Chat component
 */

interface ChatWindowProps {
  thread?: IChatThread;
  patient?: IPatient;
  onlyShowUnreadIndicator?: boolean;
  isLoading?: boolean;
}

const ChatWindow = ({
  thread: chatThread,
  patient: chatPatient,
  onlyShowUnreadIndicator = false,
  isLoading
}: ChatWindowProps): JSX.Element => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const patient = chatPatient || chatThread.patient;
  const {
    startChat: _startChat,
    getThreadByPatientIds,
    threads: contextThreads
  } = useChats();
  const [newThread, setNewThread] = useState<IChatThread>(null);
  const [thread, setThread] = useState<IChatThread>(undefined);
  const { pathname, search } = useLocation();
  const isLookingAtOnePatient =
    pathname.includes('/patient/') || search.includes('patientId=');
  const fetchedForPatientIdRef = useRef<number | null>(null);
  const startedInContextThreadIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (isLoading) return;
    if (isLookingAtOnePatient) {
      // Prefer thread from context when available
      const ctxThread = contextThreads?.find(
        (t) => t?.patientId === patient.id
      );
      if (ctxThread) {
        setThread(ctxThread);
        fetchedForPatientIdRef.current = patient.id;
        return;
      }
      if (fetchedForPatientIdRef.current === patient.id) {
        return;
      }
      getThreadByPatientIds([patient.id]).then((response) => {
        const threads = response.threads;
        if (threads.length > 0) {
          const fetched = threads[0];
          setThread(fetched);
          // Insert into context only if it's not already there and we haven't inserted it before
          const existsInContext = contextThreads?.some(
            (t) => t?.id === fetched.id
          );
          if (
            !existsInContext &&
            startedInContextThreadIdRef.current !== fetched.id
          ) {
            _startChat(fetched);
            startedInContextThreadIdRef.current = fetched.id;
          }
        } else {
          setThread(null);
        }
        fetchedForPatientIdRef.current = patient.id;
      });
      return;
    }
    setThread(chatThread ?? null);
    return;
  }, [isLoading, isLookingAtOnePatient, patient.id]);

  // Keep the drawer's thread in sync with context updates
  useEffect(() => {
    if (isLoading) return;
    const ctxThread = contextThreads?.find((t) => t?.patientId === patient.id);
    if (ctxThread) {
      setThread(ctxThread);
    }
  }, [contextThreads, isLoading]);

  const handleClose = () => {
    setIsChatOpen(false);
  };

  const startChat = (patient: IPatient, brandId: number) => {
    EnsureThread({
      patientId: patient.id,
      brandId
    }).then((response: EnsureThreadResponse) => {
      if (response.status === 'FAIL') {
        Modal.error({
          title: 'Error Starting Chat',
          content: response.message
        });
        return;
      }
      const thread = response.thread;
      _startChat(thread);
      setNewThread(thread);
      setIsChatOpen(true);
    });
  };
  if (isLoading || thread === undefined) {
    return null;
  }
  return (
    <>
      <Drawer
        className="border-l-solid border-4 border-light-grey"
        style={{
          paddingBottom: '40px' // Offset for chat entry
        }}
        size="70%"
        isOpen={isChatOpen}
        onClose={handleClose}
        title={
          <span className="font-normal">
            <span className="ml-6 mb-0 font-serif inline-block">
              {GetFullName(patient)}
            </span>
            <span className="text-small">
              <span className="ml-6 uppercase font-bold">CSID</span>{' '}
              {patient.id}
            </span>
            <span className="text-small">
              <span className="ml-6 uppercase font-bold">Phone</span>
              <span className="ml-2">{FormatPhone(patient.phone)}</span>
              {patient.mrn && (
                <>
                  <span className="ml-6 uppercase font-bold">MRN</span>
                  <span className="ml-2">{patient.mrn}</span>
                </>
              )}
              {patient.dob ? (
                <React.Fragment>
                  <span className="ml-6 uppercase font-bold">DOB</span>
                  <span className="ml-2">{patient.dob}</span>
                </React.Fragment>
              ) : null}
            </span>
            <Link to={`/patient/${patient.id}/`} className="text-small ml-6">
              <Icon icon={faExternalLinkAlt} />
            </Link>
          </span>
        }
        portalClassName="z-30"
      >
        <ChatUI thread={newThread ?? thread} isInDrawer />
      </Drawer>
      {thread === null ? (
        <Popover
          position="auto-end"
          content={
            <div style={{ minWidth: '180px' }}>
              <SelectPatientCard
                patient={patient}
                isSelected={true}
                isForChat
                startChat={startChat}
              />
            </div>
          }
        >
          <Button className="pt-3 text-small" variant="minimal">
            <Icon icon={faCommentsAlt} className="text-small mr-4" />
            New Chat
          </Button>
        </Popover>
      ) : (
        <ChatButton
          onlyShowUnreadIndicator={onlyShowUnreadIndicator}
          numUnread={thread?.numUnread || 0}
          setIsChatOpen={setIsChatOpen}
        />
      )}
    </>
  );
};

/**
 * Chats component
 */

interface ChatsProps {
  patient: IPatient;
  onlyShowUnreadIndicator?: boolean;
}

const Chats = ({
  patient,
  onlyShowUnreadIndicator = false
}: ChatsProps): JSX.Element => {
  const { threads, isLoading } = useChats();

  let thread: IChatThread;
  for (const _thread of threads) {
    if (_thread.patientId === patient.id) {
      thread = _thread;
      break;
    }
  }
  return (
    <ChatWindow
      thread={thread}
      patient={patient}
      onlyShowUnreadIndicator={onlyShowUnreadIndicator}
      isLoading={isLoading}
    />
  );
};

export default Chats;
